import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import type { LoyaltyCard } from '../api/types';
import { Alert, Spinner, formatDate } from '../components/common';

/**
 * The customer's stamp card, laid out like the printed one so the two are
 * obviously the same programme.
 */
export function LoyaltyPage() {
  const [card, setCard] = useState<LoyaltyCard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .loyaltyCard()
      .then(setCard)
      .catch((e: Error) => setError(e.message));
  }, []);

  if (error) return <Alert>{error}</Alert>;
  if (!card) return <Spinner label="Loading your card…" />;

  if (!card.enabled) {
    return <Alert tone="info">The loyalty programme is paused at the moment.</Alert>;
  }

  return (
    <section className="page page--narrow">
      <h1>Your loyalty card</h1>
      <p className="muted">
        Buy {card.stampsPerReward} and your next one is free — the same card you get in store.
      </p>

      {card.availableRewards > 0 && (
        <Alert tone="success">
          <strong>
            You have {card.availableRewards} free item{card.availableRewards > 1 ? 's' : ''} waiting.
          </strong>{' '}
          Use it at checkout, or show this screen at the counter.
        </Alert>
      )}

      <div className="stampcard">
        {Array.from({ length: card.stampsPerReward }, (_, index) => (
          <div
            key={index}
            className={index < card.stampCount ? 'stamp is-filled' : 'stamp'}
            aria-label={index < card.stampCount ? `Stamp ${index + 1}, earned` : `Stamp ${index + 1}, empty`}
          >
            {index < card.stampCount ? '🥤' : index + 1}
          </div>
        ))}
        <div className={card.availableRewards > 0 ? 'stamp stamp--free is-filled' : 'stamp stamp--free'}>
          {card.availableRewards > 0 ? '🎉' : 'FREE'}
        </div>
      </div>

      <p className="center">
        {card.stampsUntilReward === 0 ? (
          <strong className="hint-good">Your free item is ready to claim!</strong>
        ) : (
          <>
            <strong>{card.stampsUntilReward}</strong> more to go until your free item.
          </>
        )}
      </p>

      <div className="panel">
        <h2 className="panel__title">What earns a stamp</h2>
        <p>{card.qualifyingCategories.join(' · ')}</p>
        <p className="muted small">
          One stamp per order, however many you buy — exactly like the card at the counter.
          Hot drinks, teas, snacks and ice cream don&apos;t earn stamps.
        </p>
      </div>

      <div className="panel">
        <h2 className="panel__title">Your totals</h2>
        <div className="stats">
          <div>
            <strong>{card.lifetimeStamps}</strong>
            <span className="muted small">stamps earned</span>
          </div>
          <div>
            <strong>{card.lifetimeRewards}</strong>
            <span className="muted small">free items earned</span>
          </div>
          <div>
            <strong>{card.availableRewards}</strong>
            <span className="muted small">waiting to use</span>
          </div>
        </div>
      </div>

      {card.activity.length > 0 && (
        <div className="panel">
          <h2 className="panel__title">Recent activity</h2>
          <ul className="activity">
            {card.activity.map((entry, index) => (
              <li key={`${entry.at}-${index}`}>
                <span>{entry.description}</span>
                <span className="muted small">{formatDate(entry.at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Link to="/" className="btn btn--primary btn--block">
        Order something
      </Link>

      <Link to="/change-password" className="btn btn--ghost btn--block">
        Change my password
      </Link>
    </section>
  );
}
